import { Box, Card, Grid, Typography, Fade } from '@mui/material'
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    ChartDataLabels
);

const chartBoxStyle = (isDarkMode) => ({
    border: isDarkMode ? '1px solid #3d4a62' : '1px solid #e0e0e0',
    borderRadius: '12px',
    boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
    p: 2,
    background: isDarkMode ? 'rgba(31, 42, 64, 0.5)' : 'rgba(255, 255, 255, 0.5)'
});

const tooltipStyle = (isDarkMode) => ({
    backgroundColor: isDarkMode ? '#2d3a52' : '#fff',
    titleColor: isDarkMode ? '#e0e0e0' : '#2c3e50',
    bodyColor: isDarkMode ? '#b0bec5' : '#5f6368',
    borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0',
    borderWidth: 1,
    padding: 12,
    displayColors: false,
});

const datalabelsVertical = (isDarkMode) => ({
    anchor: 'end',
    align: 'top',
    offset: 4,
    color: isDarkMode ? '#fff' : '#000',
    font: { size: 11, weight: 'bold' },
    formatter: (value) => value
});

const datalabelsHorizontal = (isDarkMode) => ({
    anchor: 'end',
    align: 'end',
    color: isDarkMode ? '#fff' : '#000',
    font: { size: 11, weight: 'bold' },
    formatter: (value) => value
});

const ChartPrioridad = ({ data, isDarkMode }) => {
    if (!data || Object.keys(data).length === 0) return null;
    return (
        <Grid item xs={12} md={4}>
            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 700, fontSize: 14, mb: 2 }}>
                Por Prioridad
            </Typography>
            <Box sx={{ height: 200, ...chartBoxStyle(isDarkMode) }}>
                <Bar
                    data={{
                        labels: Object.keys(data),
                        datasets: [{
                            label: 'Tickets',
                            data: Object.values(data),
                            backgroundColor: Object.keys(data).map(key => {
                                const colors = { 'ALTA': '#ef5350', 'MEDIA': '#ff9800', 'BAJA': '#66bb6a', 'URGENTE': '#ab47bc' };
                                return colors[key] || '#42a5f5';
                            }),
                            borderColor: Object.keys(data).map(key => {
                                const colors = { 'ALTA': '#e53935', 'MEDIA': '#f57c00', 'BAJA': '#43a047', 'URGENTE': '#8e24aa' };
                                return colors[key] || '#1e88e5';
                            }),
                            borderWidth: 2,
                            borderRadius: 6,
                        }]
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: { padding: { top: 25 } },
                        plugins: {
                            legend: { display: false },
                            datalabels: datalabelsVertical(isDarkMode),
                            tooltip: tooltipStyle(isDarkMode),
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 11 }, precision: 0 },
                                grid: { color: isDarkMode ? '#3d4a62' : '#e0e0e0', drawBorder: false }
                            },
                            x: {
                                ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 11, weight: 600 } },
                                grid: { display: false }
                            }
                        }
                    }}
                />
            </Box>
        </Grid>
    );
};

const ChartTipoTicket = ({ data, isDarkMode }) => {
    if (!data || data.length === 0) return null;
    return (
        <Grid item xs={12} md={4}>
            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 700, fontSize: 14, mb: 2 }}>
                Por Tipo de Ticket
            </Typography>
            <Box sx={{ height: 200, ...chartBoxStyle(isDarkMode) }}>
                <Bar
                    data={{
                        labels: data.map(tt => tt.nombre.length > 15 ? tt.nombre.substring(0, 15) + '...' : tt.nombre),
                        datasets: [{
                            label: 'Tickets',
                            data: data.map(tt => tt.total),
                            backgroundColor: '#26c6da',
                            borderColor: '#00acc1',
                            borderWidth: 2,
                            borderRadius: 6,
                        }]
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: { padding: { top: 25 } },
                        plugins: {
                            legend: { display: false },
                            datalabels: datalabelsVertical(isDarkMode),
                            tooltip: {
                                ...tooltipStyle(isDarkMode),
                                callbacks: {
                                    title: (context) => data[context[0].dataIndex].nombre
                                }
                            }
                        },
                        scales: {
                            y: {
                                beginAtZero: true,
                                ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 11 }, precision: 0 },
                                grid: { color: isDarkMode ? '#3d4a62' : '#e0e0e0', drawBorder: false }
                            },
                            x: {
                                ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 10, weight: 600 }, maxRotation: 45, minRotation: 45 },
                                grid: { display: false }
                            }
                        }
                    }}
                />
            </Box>
        </Grid>
    );
};

const ChartEtapa = ({ data, isDarkMode }) => {
    if (!data || Object.keys(data).length === 0) return null;
    const entries = Object.entries(data).sort((a, b) => b[1] - a[1]);
    return (
        <Grid item xs={12} md={6} lg={4}>
            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 700, fontSize: 14, mb: 2 }}>
                Por Etapa
            </Typography>
            <Box sx={{ height: Math.max(200, Object.keys(data).length * 32), ...chartBoxStyle(isDarkMode) }}>
                <Bar
                    data={{
                        labels: entries.map(([key]) => key || 'Sin etapa'),
                        datasets: [{
                            label: 'Tickets',
                            data: entries.map(([, value]) => value),
                            backgroundColor: '#5c6bc0',
                            borderColor: '#3f51b5',
                            borderWidth: 2,
                            borderRadius: 4,
                            barThickness: 18,
                        }]
                    }}
                    options={{
                        indexAxis: 'y',
                        responsive: true,
                        maintainAspectRatio: false,
                        layout: { padding: { right: 30 } },
                        plugins: {
                            legend: { display: false },
                            datalabels: datalabelsHorizontal(isDarkMode),
                            tooltip: tooltipStyle(isDarkMode),
                        },
                        scales: {
                            x: {
                                beginAtZero: true,
                                ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 11 }, precision: 0 },
                                grid: { color: isDarkMode ? '#3d4a62' : '#e0e0e0', drawBorder: false }
                            },
                            y: {
                                ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 11, weight: 600 } },
                                grid: { display: false }
                            }
                        }
                    }}
                />
            </Box>
        </Grid>
    );
};

const ChartNivelAdm = ({ data, isDarkMode }) => {
    if (!data || data.length === 0) return null;
    const sorted = [...data].sort((a, b) => b.total - a.total);
    return (
        <Grid item xs={12} md={6}>
            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 700, fontSize: 14, mb: 2 }}>
                Por Nivel Administrativo
            </Typography>
            <Box sx={{ height: Math.max(200, data.length * 28), maxHeight: 500, overflow: 'auto', ...chartBoxStyle(isDarkMode) }} className='side'>
                <Box sx={{ height: Math.max(200, data.length * 28) }}>
                    <Bar
                        data={{
                            labels: sorted.map(n => n.nombre || 'Sin nivel'),
                            datasets: [{
                                label: 'Tickets',
                                data: sorted.map(n => n.total),
                                backgroundColor: '#ffa726',
                                borderColor: '#fb8c00',
                                borderWidth: 2,
                                borderRadius: 4,
                                barThickness: 16,
                            }]
                        }}
                        options={{
                            indexAxis: 'y',
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: { padding: { right: 30 } },
                            plugins: {
                                legend: { display: false },
                                datalabels: datalabelsHorizontal(isDarkMode),
                                tooltip: tooltipStyle(isDarkMode),
                            },
                            scales: {
                                x: {
                                    beginAtZero: true,
                                    ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 11 }, precision: 0 },
                                    grid: { color: isDarkMode ? '#3d4a62' : '#e0e0e0', drawBorder: false }
                                },
                                y: {
                                    ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 10, weight: 600 } },
                                    grid: { display: false }
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Grid>
    );
};

const ChartGrupo = ({ data, isDarkMode }) => {
    if (!data || data.length === 0) return null;
    const sorted = [...data].sort((a, b) => b.total - a.total);
    return (
        <Grid item xs={12} md={6}>
            <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#2c3e50', fontWeight: 700, fontSize: 14, mb: 2 }}>
                Por Grupo
            </Typography>
            <Box sx={{ height: Math.max(200, data.length * 28), maxHeight: 500, overflow: 'auto', ...chartBoxStyle(isDarkMode) }} className='side'>
                <Box sx={{ height: Math.max(200, data.length * 28) }}>
                    <Bar
                        data={{
                            labels: sorted.map(g => g.nombre || 'Sin grupo'),
                            datasets: [{
                                label: 'Tickets',
                                data: sorted.map(g => g.total),
                                backgroundColor: '#5c6bc0',
                                borderColor: '#3f51b5',
                                borderWidth: 2,
                                borderRadius: 4,
                                barThickness: 16,
                            }]
                        }}
                        options={{
                            indexAxis: 'y',
                            responsive: true,
                            maintainAspectRatio: false,
                            layout: { padding: { right: 30 } },
                            plugins: {
                                legend: { display: false },
                                datalabels: datalabelsHorizontal(isDarkMode),
                                tooltip: tooltipStyle(isDarkMode),
                            },
                            scales: {
                                x: {
                                    beginAtZero: true,
                                    ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 11 }, precision: 0 },
                                    grid: { color: isDarkMode ? '#3d4a62' : '#e0e0e0', drawBorder: false }
                                },
                                y: {
                                    ticks: { color: isDarkMode ? '#b0bec5' : '#7f8c8d', font: { size: 10, weight: 600 } },
                                    grid: { display: false }
                                }
                            }
                        }}
                    />
                </Box>
            </Box>
        </Grid>
    );
};

const DashboardCharts = ({ dashboard, isDarkMode }) => {
    if (!dashboard) return null;

    return (
        <Box sx={{ mt: 2 }}>
            <Fade in={true} timeout={700}>
                <Card sx={{
                    background: isDarkMode
                        ? 'linear-gradient(135deg, #1F2A40 0%, #2d3a52 100%)'
                        : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                    borderRadius: '16px',
                    boxShadow: isDarkMode
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.06)',
                    border: isDarkMode ? '1px solid #3d4a62' : '1px solid #e0e0e0',
                    p: 2.5,
                }}>
                    <Grid container spacing={3}>
                        <ChartPrioridad data={dashboard.porPrioridad} isDarkMode={isDarkMode} />
                        <ChartTipoTicket data={dashboard.porTipoTicket} isDarkMode={isDarkMode} />
                        <ChartEtapa data={dashboard.porStage} isDarkMode={isDarkMode} />
                        <ChartNivelAdm data={dashboard.porNivelAdm} isDarkMode={isDarkMode} />
                        <ChartGrupo data={dashboard.porGrupo} isDarkMode={isDarkMode} />
                    </Grid>
                </Card>
            </Fade>
        </Box>
    );
};

export default DashboardCharts;
